import { hash } from '@ember/helper';
import { ensureSafeComponent } from '@embroider/util';
import Component from '@glimmer/component';
import dayjs from 'dayjs';
import {
  ISO_DAYS,
  type OtaId,
} from 'frontend-utils/utils/constants/constant-values';
import GeneralCalendarsDaysDayLoading from 'lumen/components/general/calendars/days/day-loading';
import type { Day, Period } from 'lumen/models';

interface GeneralCalendarsCalendarBaseSignature {
  Args: {
    calenderHeaderClass?: string;
    containerHeight: number;
    dayComponent: unknown;
    hasLegend?: boolean;
    isLoading?: boolean;
    los?: number;
    message: unknown;
    minDayComponentHeight: number;
    model?: Day[] | Period[] | null;
    otaId?: OtaId;
    reservedHeight: number;
  };
  Blocks: {
    dayComponent: [unknown];
    legend: [];
  };
}

export default class GeneralCalendarsCalendarBase extends Component<GeneralCalendarsCalendarBaseSignature> {
  // Clone array (otherwise dayjs config is messed up)
  weekdays = [...dayjs.weekdays()];

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get containerHeight() {
    return this.args.containerHeight ?? 850;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get dayComponentHeight() {
    // @ts-expect-error: Incorrect type
    const nbRows = this.args.model.length / 7;
    const minCalendarDayHeight = this.minDayComponentHeight;
    const minCalendarAllRowsHeight = minCalendarDayHeight * nbRows;

    let reservedHeight = 0;

    // Provide backwards compatible option to override the reservedHeight (calendar-base is no longer used in Rate Insight alone)
    if (!this.args.reservedHeight) {
      // subtract the header height, main content paddings and calendar header from the browser height
      reservedHeight = 60 + 60 + 40 + 40 + 65;

      if (this.hasLegend) {
        reservedHeight += 65;
      }
    } else {
      reservedHeight = this.args.reservedHeight;
    }

    const correctedContainerHeight =
      this.containerHeight - reservedHeight > minCalendarAllRowsHeight
        ? this.containerHeight - reservedHeight
        : minCalendarAllRowsHeight;

    // Added Math.floor here to not end up with .xxx pixels. Floor is the safest option to prevent any unwanted scrollbars
    return Math.floor(correctedContainerHeight / nbRows);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get hasLegend() {
    return this.args.hasLegend ?? false;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get minDayComponentHeight() {
    return this.args.minDayComponentHeight ?? 115;
  }

  constructor() {
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line prefer-rest-params
    super(...arguments);

    if (dayjs.localeData().firstDayOfWeek() === ISO_DAYS.mon) {
      // @ts-expect-error: Incorrect type
      this.weekdays.push(this.weekdays.shift());
    }
  }

  <template>
    <section class="ins-calendar relative">
      <div class="card-tile__content">
        <div
          class="ins-calendar-header
            {{if @calenderHeaderClass @calenderHeaderClass}}"
        >
          {{#each this.weekdays as |weekday|}}
            <div class="ins-calendar-day">
              {{weekday}}
            </div>
          {{/each}}
        </div>
        <div class="ins-calendar-content">
          {{! @glint-expect-error: Incorrect type }}
          {{#each @model as |period index|}}
            {{#if @isLoading}}
              <GeneralCalendarsDaysDayLoading
                @dayComponentHeight={{this.dayComponentHeight}}
              />
            {{else}}
              {{yield
                (hash
                  day=(component
                    (ensureSafeComponent @dayComponent)
                    dayComponentHeight=this.dayComponentHeight
                    dayIndex=index
                    los=@los
                    model=period
                    otaId=@otaId
                  )
                )
                to="dayComponent"
              }}
            {{/if}}
          {{/each}}
        </div>
        {{! @glint-expect-error: Incorrect type }}
        {{#if @message.showOverlay}}
          <div class="message-overlay">
            <div class="message-wrapper">
              {{! @glint-expect-error: Incorrect type }}
              {{@message.message}}
            </div>
          </div>
        {{/if}}
        {{yield to="legend"}}
      </div>
    </section>
  </template>
}
